import { Navigate } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import type { SocialReportResponse } from "contracts/api/social/report";
import { For, Show } from "solid-js";
import { QUERY_ROOT } from "../../enums/shell";
import { api } from "../../store/api";
import { socialEnabled } from "../../store/demo";
import { EmptyState } from "../../ui/EmptyState";
import { ModuleShell } from "../scan/ModuleShell";
import { ScanRouteSurface } from "../scan/ScanRouteChrome";
import { ScanRouteHeader } from "../scan/ScanRouteHeader";
import { createWebsiteStore } from "../website/store";
import { SocialReportPostCard } from "./SocialReportPostCard";
import { SocialReportProfileCard } from "./SocialReportProfileCard";
import { SocialReportSummary } from "./SocialReportSummary";

/**
 * The client-facing social report. The route is website-scoped like every
 * other module, but social data belongs to the owning Client, so the page
 * resolves the client id from the website overview it already loads for the
 * shell and reads the client-wide report with it.
 */
export function SocialReport(props: { websiteId: string }) {
  return (
    <Show
      when={socialEnabled()}
      fallback={<Navigate href={`/w/${props.websiteId}/overview`} />}
    >
      <EnabledSocialReport websiteId={props.websiteId} />
    </Show>
  );
}

function EnabledSocialReport(props: { websiteId: string }) {
  const context = createWebsiteStore(props.websiteId);
  const clientId = () => context.data()?.site.clientId ?? null;
  const report = useQuery(() => ({
    queryKey: [QUERY_ROOT.SOCIAL_REPORT, clientId()],
    queryFn: () => {
      const id = clientId();
      if (!id) throw new Error("Client id not resolved yet");
      return api.social.readSocialReport(id);
    },
    enabled: socialEnabled() && clientId() !== null,
  }));

  return (
    <ModuleShell context={context} activeModule="social">
      <ScanRouteSurface class="social-report">
        <ScanRouteHeader
          websiteId={props.websiteId}
          section="Social"
          title="Social"
          subtitle="Registered public profiles, the posts captured from them, and what the analysis concluded — with the evidence behind every conclusion."
        />
        <Show
          when={report.data}
          keyed
          fallback={
            <div class="loading signal-loading">Loading social report…</div>
          }
        >
          {(data) => <ReportBody report={data} />}
        </Show>
      </ScanRouteSurface>
    </ModuleShell>
  );
}

function ReportBody(props: { report: SocialReportResponse }) {
  return (
    <Show
      when={props.report.profiles.length > 0}
      fallback={
        <EmptyState
          title="No social profiles are registered"
          hint="Once public profiles are registered for this client, their posts and analysis will appear here."
        />
      }
    >
      <SocialReportSummary profiles={props.report.profiles} />
      <For each={props.report.profiles}>
        {(profile) => {
          const posts = () =>
            props.report.posts.filter((post) => post.profileId === profile.id);
          return (
            <section class="social-report-profile">
              <SocialReportProfileCard profile={profile} />
              <div class="social-report-posts">
                <For each={posts()}>
                  {(post) => <SocialReportPostCard post={post} />}
                </For>
              </div>
            </section>
          );
        }}
      </For>
    </Show>
  );
}
